import {
  SELECTION_HULL_DURATION,
  SELECTION_HULL_KEY_DURATION,
  type SelectionHullCause,
} from '@/src/renderer/lib/motion/selectionHullMotion'

/**
 * **Woher die letzte Auswahl kam** (#293): Ein Schritt per ↑/↓ (auch mit Shift) fährt die Hülle im kurzen
 * Takt, alles andere (Zeiger, andere Tasten, ⌘↑/⌘↓) im langen (Sven, 09.10.2026,
 * `tools/arrow-step-motion-prototype`).
 *
 * Abgehört wird global in der Capture-Phase, also vor den Tasten-Griffen der Listen und Seiten. So muss
 * keine Seite durchreichen, wie sie ihre Auswahl geändert hat — und die Dauer für Schrift und Haken
 * (`--selection-hull-duration` an `:root`, gelesen vom Mixin `selection-hull-follow`) steht fest, bevor
 * React die Zeilen umfärbt. Gesetzt wird sie nur, wenn sie sich ändert.
 */

/** So lange nach einem Druck auf ↑/↓ gilt eine neue Auswahl als Pfeil-Schritt. */
const ARROW_WINDOW_MS = 300

let lastArrowAt = -Infinity
let installed = false

function setRowDuration(seconds: number): void {
  const value = `${Math.round(seconds * 1000)}ms`
  const root = document.documentElement
  if (root.style.getPropertyValue('--selection-hull-duration') !== value) {
    root.style.setProperty('--selection-hull-duration', value)
  }
}

/**
 * Hört ab jetzt mit; mehrfach aufgerufen, bleibt es bei einem Paar Listener. Die Ebene der Hülle ruft
 * es beim ersten Einhängen auf.
 */
export function installSelectionInput(): void {
  if (installed || typeof window === 'undefined') return
  installed = true
  window.addEventListener('keydown', (e: KeyboardEvent) => {
    // Mit ⌘ (Mac) springt ↑/↓ an Anfang oder Ende der Liste wie Home/End — ein Sprung, kein Schritt.
    const step = (e.key === 'ArrowDown' || e.key === 'ArrowUp') && !e.metaKey && !e.ctrlKey && !e.altKey
    if (step) {
      lastArrowAt = performance.now()
      setRowDuration(SELECTION_HULL_KEY_DURATION)
    } else {
      lastArrowAt = -Infinity
      setRowDuration(SELECTION_HULL_DURATION)
    }
  }, true)
  window.addEventListener('pointerdown', () => {
    lastArrowAt = -Infinity
    setRowDuration(SELECTION_HULL_DURATION)
  }, true)
}

/**
 * Wie die Hülle eine geänderte Auswahl fährt: kurz nach ↑/↓ im kurzen Takt (`key`), sonst im langen.
 *
 * @example
 * const cause = instant ? 'layout' : selectionInputCause()
 */
export function selectionInputCause(now = performance.now()): Exclude<SelectionHullCause, 'layout'> {
  return now - lastArrowAt <= ARROW_WINDOW_MS ? 'key' : 'selection'
}
